import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Reveal, ChapterHead } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Archive = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/entries`)
      .then((r) => setEntries(r.data))
      .catch(() => setEntries([]))
      .finally(() => setLoading(false));
  }, []);

  const years = useMemo(() => {
    const map = {};
    entries.forEach((e) => {
      (map[e.year] = map[e.year] || []).push(e);
    });
    return Object.keys(map)
      .map(Number)
      .sort((a, b) => b - a)
      .map((y) => ({ year: y, items: [...map[y]].reverse() }));
  }, [entries]);

  return (
    <section id="archive" className="px-6 sm:px-12 lg:px-20 py-24 sm:py-32 max-w-7xl mx-auto" data-testid="archive-section">
      <ChapterHead index="CHAPTER 06" title="THE ARCHIVE" />

      {loading && (
        <p className="font-meta text-xs tracking-[0.3em] text-neutral-500" data-testid="archive-loading">
          LOADING THE RECORD…
        </p>
      )}

      {!loading && years.length === 0 && (
        <p className="font-meta text-xs tracking-[0.3em] text-neutral-500" data-testid="archive-empty">
          THE ARCHIVE OPENS SOON.
        </p>
      )}

      {years.map(({ year, items }) => (
        <div key={year} className="mb-16 sm:mb-24" data-testid={`archive-year-${year}`}>
          <Reveal>
            <div className="flex items-baseline gap-6 mb-2">
              <span className="font-display text-5xl sm:text-7xl font-extrabold tracking-tighter text-transparent" style={{ WebkitTextStroke: "1.5px rgba(255,255,255,0.65)" }}>
                {year}
              </span>
              <span className="font-meta text-[10px] sm:text-xs tracking-[0.3em] text-neutral-500">
                {items.length} {items.length === 1 ? "ENTRY" : "ENTRIES"}
              </span>
            </div>
          </Reveal>
          <div className="border-t border-white/10">
            {items.map((e) => (
              <Reveal key={e.slug}>
                <Link
                  to={`/archive/${e.slug}`}
                  data-testid={`archive-entry-${e.slug}`}
                  className="group grid grid-cols-12 gap-3 sm:gap-6 items-baseline border-b border-white/10 py-6 sm:py-8 px-2 sm:px-4 -mx-2 sm:-mx-4 hover:bg-white hover:text-black transition-colors duration-300"
                >
                  <span className="col-span-3 sm:col-span-2 font-meta text-[10px] sm:text-xs tracking-[0.2em] text-neutral-500 group-hover:text-black/50 transition-colors duration-300">
                    {e.date_display}
                  </span>
                  <span className="col-span-9 sm:col-span-6 font-display text-xl sm:text-3xl font-bold uppercase tracking-tight leading-tight">
                    {e.title}
                  </span>
                  <span className="hidden sm:block sm:col-span-3 font-meta text-[10px] tracking-[0.2em] text-neutral-500 group-hover:text-black/50 transition-colors duration-300">
                    {e.type} — {e.themes.slice(0, 2).join(" / ")}
                  </span>
                  <span className="hidden sm:flex sm:col-span-1 justify-end font-display text-2xl group-hover:translate-x-2 transition-transform duration-300">
                    →
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      ))}
    </section>
  );
};
